import React from "react";
import { Plus, Loader2, MapPinOff } from "lucide-react";
import { PatientAddress } from "./types";
import AddressCard from "./AddressCard";

interface AddressListProps {
  loading: boolean;
  addresses: PatientAddress[];
  selectedAddress: PatientAddress | null;
  onSelectAddress: (address: PatientAddress) => void;
  onAddNew: () => void;
}

export const AddressList: React.FC<AddressListProps> = ({
  loading,
  addresses,
  selectedAddress,
  onSelectAddress,
  onAddNew,
}) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3 text-slate-400">
        <Loader2 size={24} className="animate-spin text-emerald-500" />
        <span className="text-xs font-semibold text-slate-500">
          Loading saved addresses...
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
          Saved Patient Addresses ({addresses.length})
        </span>
        <button
          onClick={onAddNew}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors cursor-pointer shadow-xs"
        >
          <Plus size={14} /> Add New Address
        </button>
      </div>

      {addresses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 px-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 text-center gap-2">
          <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center">
            <MapPinOff size={20} />
          </div>
          <p className="text-xs font-bold text-slate-700">No saved addresses found</p>
          <p className="text-[0.7rem] text-slate-400 max-w-xs">
            This patient does not have any saved delivery address yet.
          </p>
          <button
            onClick={onAddNew}
            className="mt-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors cursor-pointer shadow-xs"
          >
            Create Address Now
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5 max-h-[280px] overflow-auto pr-1">
          {addresses.map((addr) => (
            <AddressCard
              key={addr.id || `${addr.latitude}_${addr.longitude}`}
              address={addr}
              isSelected={selectedAddress?.id === addr.id}
              onSelect={onSelectAddress}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressList;
